import { useState, useEffect, useRef, useCallback } from "react"

/**
 * Simulated audio amplitude hook for visual effects.
 * 
 * NOTE: Real Web Audio API beat detection is NOT possible with YouTube IFrame
 * due to cross-origin restrictions. This hook provides simulated amplitude
 * values that respond to play/pause state for smooth visual animations.
 * 
 * Usage:
 *   const amplitude = useAudioAnalyser(isPlaying)
 *   // amplitude: 0-1 value, 0 when paused, oscillating 0.3-0.9 when playing
 */
export function useAudioAnalyser(isPlaying: boolean): number {
  const [amplitude, setAmplitude] = useState(0)
  const animationRef = useRef<number | null>(null)
  const startTimeRef = useRef<number>(0)

  const animate = useCallback((timestamp: number) => {
    if (!startTimeRef.current) {
      startTimeRef.current = timestamp
    }
    
    const elapsed = timestamp - startTimeRef.current
    
    // Create organic-feeling oscillation using multiple sine waves
    // Primary wave (slower, larger)
    const primary = Math.sin(elapsed * 0.003) * 0.2
    // Secondary wave (faster, smaller)
    const secondary = Math.sin(elapsed * 0.007) * 0.1
    // Tertiary wave (fastest, smallest - adds texture)
    const tertiary = Math.sin(elapsed * 0.013) * 0.05
    
    // Combine waves and normalize to 0.35-0.85 range
    const combined = 0.6 + primary + secondary + tertiary
    
    setAmplitude(Math.max(0.3, Math.min(0.9, combined)))
    
    animationRef.current = requestAnimationFrame(animate)
  }, [])

  useEffect(() => {
    if (isPlaying) {
      startTimeRef.current = 0
      animationRef.current = requestAnimationFrame(animate)
    } else {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
        animationRef.current = null
      }
      // Smoothly return to 0 when paused
      setAmplitude(0)
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [isPlaying, animate])

  return amplitude
}
